/**
 * Consolidated country codes and data for B2B components
 * Replaces duplicate country code definitions across components
 */

export interface CountryCode {
  readonly code: string;
  readonly country: string;
  readonly flag: string;
}

export interface CountryData {
  readonly code: string;
  readonly name: string;
  readonly region: string;
  readonly flag: string;
  readonly phoneCode?: string;
}

/**
 * Phone country codes for form inputs
 * Used in registration and profile forms
 */
export const B2B_PHONE_COUNTRY_CODES: readonly CountryCode[] = [
  { code: "+44", country: "unitedKingdom", flag: "🇬🇧" },
  { code: "+1", country: "unitedStates", flag: "🇺🇸" },
  { code: "+1", country: "canada", flag: "🇨🇦" },
  { code: "+33", country: "france", flag: "🇫🇷" },
  { code: "+49", country: "germany", flag: "🇩🇪" },
  { code: "+39", country: "italy", flag: "🇮🇹" },
  { code: "+34", country: "spain", flag: "🇪🇸" },
  { code: "+31", country: "netherlands", flag: "🇳🇱" },
  { code: "+41", country: "switzerland", flag: "🇨🇭" },
  { code: "+43", country: "austria", flag: "🇦🇹" },
  { code: "+32", country: "belgium", flag: "🇧🇪" },
  { code: "+45", country: "denmark", flag: "🇩🇰" },
  { code: "+46", country: "sweden", flag: "🇸🇪" },
  { code: "+47", country: "norway", flag: "🇳🇴" },
  { code: "+358", country: "finland", flag: "🇫🇮" },
  { code: "+61", country: "australia", flag: "🇦🇺" },
  { code: "+81", country: "japan", flag: "🇯🇵" },
  { code: "+65", country: "singapore", flag: "🇸🇬" },
  { code: "+971", country: "unitedArabEmirates", flag: "🇦🇪" },
  { code: "+7", country: "russia", flag: "🇷🇺" },
  { code: "+86", country: "china", flag: "🇨🇳" },
  { code: "+82", country: "southKorea", flag: "🇰🇷" },
  { code: "+91", country: "india", flag: "🇮🇳" },
  { code: "+55", country: "brazil", flag: "🇧🇷" },
  { code: "+52", country: "mexico", flag: "🇲🇽" },
  { code: "+54", country: "argentina", flag: "🇦🇷" },
  { code: "+56", country: "chile", flag: "🇨🇱" },
  { code: "+57", country: "colombia", flag: "🇨🇴" },
  { code: "+51", country: "peru", flag: "🇵🇪" },
  { code: "+58", country: "venezuela", flag: "🇻🇪" },
  { code: "+27", country: "southAfrica", flag: "🇿🇦" },
  { code: "+234", country: "nigeria", flag: "🇳🇬" },
  { code: "+233", country: "ghana", flag: "🇬🇭" },
  { code: "+254", country: "kenya", flag: "🇰🇪" },
  { code: "+212", country: "morocco", flag: "🇲🇦" },
  { code: "+20", country: "egypt", flag: "🇪🇬" },
  { code: "+64", country: "newZealand", flag: "🇳🇿" },
  // Europe (Ukraine, Cyprus + more)
  { code: "+380", country: "ukraine", flag: "🇺🇦" },
  { code: "+357", country: "cyprus", flag: "🇨🇾" },
  { code: "+353", country: "ireland", flag: "🇮🇪" },
  { code: "+351", country: "portugal", flag: "🇵🇹" },
  { code: "+30", country: "greece", flag: "🇬🇷" },
  { code: "+420", country: "czechRepublic", flag: "🇨🇿" },
  { code: "+40", country: "romania", flag: "🇷🇴" },
  { code: "+36", country: "hungary", flag: "🇭🇺" },
  { code: "+385", country: "croatia", flag: "🇭🇷" },
  { code: "+354", country: "iceland", flag: "🇮🇸" },
  { code: "+48", country: "poland", flag: "🇵🇱" },
  { code: "+90", country: "turkey", flag: "🇹🇷" },
  // Asia & Middle East
  { code: "+62", country: "indonesia", flag: "🇮🇩" },
  { code: "+63", country: "philippines", flag: "🇵🇭" },
  { code: "+66", country: "thailand", flag: "🇹🇭" },
  { code: "+84", country: "vietnam", flag: "🇻🇳" },
  { code: "+60", country: "malaysia", flag: "🇲🇾" },
  { code: "+972", country: "israel", flag: "🇮🇱" },
  { code: "+966", country: "saudiArabia", flag: "🇸🇦" },
] as const;

/**
 * Comprehensive country data for dropdowns and selection
 * Used in countries dropdown and business operations
 */
export const B2B_COUNTRIES: readonly CountryData[] = [
  { code: "AF", name: "Afghanistan", region: "Asia", flag: "🇦🇫", phoneCode: "+93" },
  { code: "AL", name: "Albania", region: "Europe", flag: "🇦🇱", phoneCode: "+355" },
  { code: "DZ", name: "Algeria", region: "Africa", flag: "🇩🇿", phoneCode: "+213" },
  { code: "AD", name: "Andorra", region: "Europe", flag: "🇦🇩", phoneCode: "+376" },
  { code: "AO", name: "Angola", region: "Africa", flag: "🇦🇴", phoneCode: "+244" },
  { code: "AG", name: "Antigua and Barbuda", region: "Americas", flag: "🇦🇬", phoneCode: "+1268" },
  { code: "AR", name: "Argentina", region: "Americas", flag: "🇦🇷", phoneCode: "+54" },
  { code: "AM", name: "Armenia", region: "Asia", flag: "🇦🇲", phoneCode: "+374" },
  { code: "AU", name: "Australia", region: "Oceania", flag: "🇦🇺", phoneCode: "+61" },
  { code: "AT", name: "Austria", region: "Europe", flag: "🇦🇹", phoneCode: "+43" },
  { code: "AZ", name: "Azerbaijan", region: "Asia", flag: "🇦🇿", phoneCode: "+994" },
  { code: "BS", name: "Bahamas", region: "Americas", flag: "🇧🇸", phoneCode: "+1242" },
  { code: "BH", name: "Bahrain", region: "Asia", flag: "🇧🇭", phoneCode: "+973" },
  { code: "BD", name: "Bangladesh", region: "Asia", flag: "🇧🇩", phoneCode: "+880" },
  { code: "BB", name: "Barbados", region: "Americas", flag: "🇧🇧", phoneCode: "+1246" },
  { code: "BY", name: "Belarus", region: "Europe", flag: "🇧🇾", phoneCode: "+375" },
  { code: "BE", name: "Belgium", region: "Europe", flag: "🇧🇪", phoneCode: "+32" },
  { code: "BZ", name: "Belize", region: "Americas", flag: "🇧🇿", phoneCode: "+501" },
  { code: "BJ", name: "Benin", region: "Africa", flag: "🇧🇯", phoneCode: "+229" },
  { code: "BT", name: "Bhutan", region: "Asia", flag: "🇧🇹", phoneCode: "+975" },
  { code: "BO", name: "Bolivia", region: "Americas", flag: "🇧🇴", phoneCode: "+591" },
  { code: "BA", name: "Bosnia and Herzegovina", region: "Europe", flag: "🇧🇦", phoneCode: "+387" },
  { code: "BW", name: "Botswana", region: "Africa", flag: "🇧🇼", phoneCode: "+267" },
  { code: "BR", name: "Brazil", region: "Americas", flag: "🇧🇷", phoneCode: "+55" },
  { code: "BN", name: "Brunei", region: "Asia", flag: "🇧🇳", phoneCode: "+673" },
  { code: "BG", name: "Bulgaria", region: "Europe", flag: "🇧🇬", phoneCode: "+359" },
  { code: "BF", name: "Burkina Faso", region: "Africa", flag: "🇧🇫", phoneCode: "+226" },
  { code: "BI", name: "Burundi", region: "Africa", flag: "🇧🇮", phoneCode: "+257" },
  { code: "CV", name: "Cape Verde", region: "Africa", flag: "🇨🇻", phoneCode: "+238" },
  { code: "KH", name: "Cambodia", region: "Asia", flag: "🇰🇭", phoneCode: "+855" },
  { code: "CM", name: "Cameroon", region: "Africa", flag: "🇨🇲", phoneCode: "+237" },
  { code: "CA", name: "Canada", region: "Americas", flag: "🇨🇦", phoneCode: "+1" },
  { code: "CF", name: "Central African Republic", region: "Africa", flag: "🇨🇫", phoneCode: "+236" },
  { code: "TD", name: "Chad", region: "Africa", flag: "🇹🇩", phoneCode: "+235" },
  { code: "CL", name: "Chile", region: "Americas", flag: "🇨🇱", phoneCode: "+56" },
  { code: "CN", name: "China", region: "Asia", flag: "🇨🇳", phoneCode: "+86" },
  { code: "CO", name: "Colombia", region: "Americas", flag: "🇨🇴", phoneCode: "+57" },
  { code: "KM", name: "Comoros", region: "Africa", flag: "🇰🇲", phoneCode: "+269" },
  { code: "CG", name: "Congo", region: "Africa", flag: "🇨🇬", phoneCode: "+242" },
  { code: "CD", name: "Democratic Republic of the Congo", region: "Africa", flag: "🇨🇩", phoneCode: "+243" },
  { code: "CR", name: "Costa Rica", region: "Americas", flag: "🇨🇷", phoneCode: "+506" },
  { code: "CI", name: "Côte d'Ivoire", region: "Africa", flag: "🇨🇮", phoneCode: "+225" },
  { code: "HR", name: "Croatia", region: "Europe", flag: "🇭🇷", phoneCode: "+385" },
  { code: "CU", name: "Cuba", region: "Americas", flag: "🇨🇺", phoneCode: "+53" },
  { code: "CY", name: "Cyprus", region: "Europe", flag: "🇨🇾", phoneCode: "+357" },
  { code: "CZ", name: "Czech Republic", region: "Europe", flag: "🇨🇿", phoneCode: "+420" },
  { code: "DK", name: "Denmark", region: "Europe", flag: "🇩🇰", phoneCode: "+45" },
  { code: "DJ", name: "Djibouti", region: "Africa", flag: "🇩🇯", phoneCode: "+253" },
  { code: "DM", name: "Dominica", region: "Americas", flag: "🇩🇲", phoneCode: "+1767" },
  { code: "DO", name: "Dominican Republic", region: "Americas", flag: "🇩🇴", phoneCode: "+1809" },
  { code: "EC", name: "Ecuador", region: "Americas", flag: "🇪🇨", phoneCode: "+593" },
  { code: "EG", name: "Egypt", region: "Africa", flag: "🇪🇬", phoneCode: "+20" },
  { code: "SV", name: "El Salvador", region: "Americas", flag: "🇸🇻", phoneCode: "+503" },
  { code: "GQ", name: "Equatorial Guinea", region: "Africa", flag: "🇬🇶", phoneCode: "+240" },
  { code: "ER", name: "Eritrea", region: "Africa", flag: "🇪🇷", phoneCode: "+291" },
  { code: "EE", name: "Estonia", region: "Europe", flag: "🇪🇪", phoneCode: "+372" },
  { code: "ET", name: "Ethiopia", region: "Africa", flag: "🇪🇹", phoneCode: "+251" },
  { code: "FJ", name: "Fiji", region: "Oceania", flag: "🇫🇯", phoneCode: "+679" },
  { code: "FI", name: "Finland", region: "Europe", flag: "🇫🇮", phoneCode: "+358" },
  { code: "FR", name: "France", region: "Europe", flag: "🇫🇷", phoneCode: "+33" },
  { code: "GA", name: "Gabon", region: "Africa", flag: "🇬🇦", phoneCode: "+241" },
  { code: "GM", name: "Gambia", region: "Africa", flag: "🇬🇲", phoneCode: "+220" },
  { code: "GE", name: "Georgia", region: "Asia", flag: "🇬🇪", phoneCode: "+995" },
  { code: "DE", name: "Germany", region: "Europe", flag: "🇩🇪", phoneCode: "+49" },
  { code: "GH", name: "Ghana", region: "Africa", flag: "🇬🇭", phoneCode: "+233" },
  { code: "GR", name: "Greece", region: "Europe", flag: "🇬🇷", phoneCode: "+30" },
  { code: "GD", name: "Grenada", region: "Americas", flag: "🇬🇩", phoneCode: "+1473" },
  { code: "GT", name: "Guatemala", region: "Americas", flag: "🇬🇹", phoneCode: "+502" },
  { code: "GN", name: "Guinea", region: "Africa", flag: "🇬🇳", phoneCode: "+224" },
  { code: "GW", name: "Guinea-Bissau", region: "Africa", flag: "🇬🇼", phoneCode: "+245" },
  { code: "GY", name: "Guyana", region: "Americas", flag: "🇬🇾", phoneCode: "+592" },
  { code: "HT", name: "Haiti", region: "Americas", flag: "🇭🇹", phoneCode: "+509" },
  { code: "HN", name: "Honduras", region: "Americas", flag: "🇭🇳", phoneCode: "+504" },
  { code: "HU", name: "Hungary", region: "Europe", flag: "🇭🇺", phoneCode: "+36" },
  { code: "IS", name: "Iceland", region: "Europe", flag: "🇮🇸", phoneCode: "+354" },
  { code: "IN", name: "India", region: "Asia", flag: "🇮🇳", phoneCode: "+91" },
  { code: "ID", name: "Indonesia", region: "Asia", flag: "🇮🇩", phoneCode: "+62" },
  { code: "IR", name: "Iran", region: "Asia", flag: "🇮🇷", phoneCode: "+98" },
  { code: "IQ", name: "Iraq", region: "Asia", flag: "🇮🇶", phoneCode: "+964" },
  { code: "IE", name: "Ireland", region: "Europe", flag: "🇮🇪", phoneCode: "+353" },
  { code: "IL", name: "Israel", region: "Asia", flag: "🇮🇱", phoneCode: "+972" },
  { code: "IT", name: "Italy", region: "Europe", flag: "🇮🇹", phoneCode: "+39" },
  { code: "JM", name: "Jamaica", region: "Americas", flag: "🇯🇲", phoneCode: "+1876" },
  { code: "JP", name: "Japan", region: "Asia", flag: "🇯🇵", phoneCode: "+81" },
  { code: "JO", name: "Jordan", region: "Asia", flag: "🇯🇴", phoneCode: "+962" },
  { code: "KZ", name: "Kazakhstan", region: "Asia", flag: "🇰🇿", phoneCode: "+7" },
  { code: "KE", name: "Kenya", region: "Africa", flag: "🇰🇪", phoneCode: "+254" },
  { code: "KI", name: "Kiribati", region: "Oceania", flag: "🇰🇮", phoneCode: "+686" },
  { code: "KP", name: "North Korea", region: "Asia", flag: "🇰🇵", phoneCode: "+850" },
  { code: "KR", name: "South Korea", region: "Asia", flag: "🇰🇷", phoneCode: "+82" },
  { code: "KW", name: "Kuwait", region: "Asia", flag: "🇰🇼", phoneCode: "+965" },
  { code: "KG", name: "Kyrgyzstan", region: "Asia", flag: "🇰🇬", phoneCode: "+996" },
  { code: "LA", name: "Laos", region: "Asia", flag: "🇱🇦", phoneCode: "+856" },
  { code: "LV", name: "Latvia", region: "Europe", flag: "🇱🇻", phoneCode: "+371" },
  { code: "LB", name: "Lebanon", region: "Asia", flag: "🇱🇧", phoneCode: "+961" },
  { code: "LS", name: "Lesotho", region: "Africa", flag: "🇱🇸", phoneCode: "+266" },
  { code: "LR", name: "Liberia", region: "Africa", flag: "🇱🇷", phoneCode: "+231" },
  { code: "LY", name: "Libya", region: "Africa", flag: "🇱🇾", phoneCode: "+218" },
  { code: "LI", name: "Liechtenstein", region: "Europe", flag: "🇱🇮", phoneCode: "+423" },
  { code: "LT", name: "Lithuania", region: "Europe", flag: "🇱🇹", phoneCode: "+370" },
  { code: "LU", name: "Luxembourg", region: "Europe", flag: "🇱🇺", phoneCode: "+352" },
  { code: "MK", name: "North Macedonia", region: "Europe", flag: "🇲🇰", phoneCode: "+389" },
  { code: "MG", name: "Madagascar", region: "Africa", flag: "🇲🇬", phoneCode: "+261" },
  { code: "MW", name: "Malawi", region: "Africa", flag: "🇲🇼", phoneCode: "+265" },
  { code: "MY", name: "Malaysia", region: "Asia", flag: "🇲🇾", phoneCode: "+60" },
  { code: "MV", name: "Maldives", region: "Asia", flag: "🇲🇻", phoneCode: "+960" },
  { code: "ML", name: "Mali", region: "Africa", flag: "🇲🇱", phoneCode: "+223" },
  { code: "MT", name: "Malta", region: "Europe", flag: "🇲🇹", phoneCode: "+356" },
  { code: "MH", name: "Marshall Islands", region: "Oceania", flag: "🇲🇭", phoneCode: "+692" },
  { code: "MR", name: "Mauritania", region: "Africa", flag: "🇲🇷", phoneCode: "+222" },
  { code: "MU", name: "Mauritius", region: "Africa", flag: "🇲🇺", phoneCode: "+230" },
  { code: "MX", name: "Mexico", region: "Americas", flag: "🇲🇽", phoneCode: "+52" },
  { code: "FM", name: "Micronesia", region: "Oceania", flag: "🇫🇲", phoneCode: "+691" },
  { code: "MD", name: "Moldova", region: "Europe", flag: "🇲🇩", phoneCode: "+373" },
  { code: "MC", name: "Monaco", region: "Europe", flag: "🇲🇨", phoneCode: "+377" },
  { code: "MN", name: "Mongolia", region: "Asia", flag: "🇲🇳", phoneCode: "+976" },
  { code: "ME", name: "Montenegro", region: "Europe", flag: "🇲🇪", phoneCode: "+382" },
  { code: "MA", name: "Morocco", region: "Africa", flag: "🇲🇦", phoneCode: "+212" },
  { code: "MZ", name: "Mozambique", region: "Africa", flag: "🇲🇿", phoneCode: "+258" },
  { code: "MM", name: "Myanmar", region: "Asia", flag: "🇲🇲", phoneCode: "+95" },
  { code: "NA", name: "Namibia", region: "Africa", flag: "🇳🇦", phoneCode: "+264" },
  { code: "NR", name: "Nauru", region: "Oceania", flag: "🇳🇷", phoneCode: "+674" },
  { code: "NP", name: "Nepal", region: "Asia", flag: "🇳🇵", phoneCode: "+977" },
  { code: "NL", name: "Netherlands", region: "Europe", flag: "🇳🇱", phoneCode: "+31" },
  { code: "NZ", name: "New Zealand", region: "Oceania", flag: "🇳🇿", phoneCode: "+64" },
  { code: "NI", name: "Nicaragua", region: "Americas", flag: "🇳🇮", phoneCode: "+505" },
  { code: "NE", name: "Niger", region: "Africa", flag: "🇳🇪", phoneCode: "+227" },
  { code: "NG", name: "Nigeria", region: "Africa", flag: "🇳🇬", phoneCode: "+234" },
  { code: "NO", name: "Norway", region: "Europe", flag: "🇳🇴", phoneCode: "+47" },
  { code: "OM", name: "Oman", region: "Asia", flag: "🇴🇲", phoneCode: "+968" },
  { code: "PK", name: "Pakistan", region: "Asia", flag: "🇵🇰", phoneCode: "+92" },
  { code: "PW", name: "Palau", region: "Oceania", flag: "🇵🇼", phoneCode: "+680" },
  { code: "PA", name: "Panama", region: "Americas", flag: "🇵🇦", phoneCode: "+507" },
  { code: "PG", name: "Papua New Guinea", region: "Oceania", flag: "🇵🇬", phoneCode: "+675" },
  { code: "PY", name: "Paraguay", region: "Americas", flag: "🇵🇾", phoneCode: "+595" },
  { code: "PE", name: "Peru", region: "Americas", flag: "🇵🇪", phoneCode: "+51" },
  { code: "PH", name: "Philippines", region: "Asia", flag: "🇵🇭", phoneCode: "+63" },
  { code: "PL", name: "Poland", region: "Europe", flag: "🇵🇱", phoneCode: "+48" },
  { code: "PT", name: "Portugal", region: "Europe", flag: "🇵🇹", phoneCode: "+351" },
  { code: "QA", name: "Qatar", region: "Asia", flag: "🇶🇦", phoneCode: "+974" },
  { code: "RO", name: "Romania", region: "Europe", flag: "🇷🇴", phoneCode: "+40" },
  { code: "RU", name: "Russia", region: "Europe", flag: "🇷🇺", phoneCode: "+7" },
  { code: "RW", name: "Rwanda", region: "Africa", flag: "🇷🇼", phoneCode: "+250" },
  { code: "KN", name: "Saint Kitts and Nevis", region: "Americas", flag: "🇰🇳", phoneCode: "+1869" },
  { code: "LC", name: "Saint Lucia", region: "Americas", flag: "🇱🇨", phoneCode: "+1758" },
  { code: "VC", name: "Saint Vincent and the Grenadines", region: "Americas", flag: "🇻🇨", phoneCode: "+1784" },
  { code: "WS", name: "Samoa", region: "Oceania", flag: "🇼🇸", phoneCode: "+685" },
  { code: "SM", name: "San Marino", region: "Europe", flag: "🇸🇲", phoneCode: "+378" },
  { code: "ST", name: "São Tomé and Príncipe", region: "Africa", flag: "🇸🇹", phoneCode: "+239" },
  { code: "SA", name: "Saudi Arabia", region: "Asia", flag: "🇸🇦", phoneCode: "+966" },
  { code: "SN", name: "Senegal", region: "Africa", flag: "🇸🇳", phoneCode: "+221" },
  { code: "RS", name: "Serbia", region: "Europe", flag: "🇷🇸", phoneCode: "+381" },
  { code: "SC", name: "Seychelles", region: "Africa", flag: "🇸🇨", phoneCode: "+248" },
  { code: "SL", name: "Sierra Leone", region: "Africa", flag: "🇸🇱", phoneCode: "+232" },
  { code: "SG", name: "Singapore", region: "Asia", flag: "🇸🇬", phoneCode: "+65" },
  { code: "SK", name: "Slovakia", region: "Europe", flag: "🇸🇰", phoneCode: "+421" },
  { code: "SI", name: "Slovenia", region: "Europe", flag: "🇸🇮", phoneCode: "+386" },
  { code: "SB", name: "Solomon Islands", region: "Oceania", flag: "🇸🇧", phoneCode: "+677" },
  { code: "SO", name: "Somalia", region: "Africa", flag: "🇸🇴", phoneCode: "+252" },
  { code: "ZA", name: "South Africa", region: "Africa", flag: "🇿🇦", phoneCode: "+27" },
  { code: "SS", name: "South Sudan", region: "Africa", flag: "🇸🇸", phoneCode: "+211" },
  { code: "ES", name: "Spain", region: "Europe", flag: "🇪🇸", phoneCode: "+34" },
  { code: "LK", name: "Sri Lanka", region: "Asia", flag: "🇱🇰", phoneCode: "+94" },
  { code: "SD", name: "Sudan", region: "Africa", flag: "🇸🇩", phoneCode: "+249" },
  { code: "SR", name: "Suriname", region: "Americas", flag: "🇸🇷", phoneCode: "+597" },
  { code: "SZ", name: "Eswatini", region: "Africa", flag: "🇸🇿", phoneCode: "+268" },
  { code: "SE", name: "Sweden", region: "Europe", flag: "🇸🇪", phoneCode: "+46" },
  { code: "CH", name: "Switzerland", region: "Europe", flag: "🇨🇭", phoneCode: "+41" },
  { code: "SY", name: "Syria", region: "Asia", flag: "🇸🇾", phoneCode: "+963" },
  { code: "TW", name: "Taiwan", region: "Asia", flag: "🇹🇼", phoneCode: "+886" },
  { code: "TJ", name: "Tajikistan", region: "Asia", flag: "🇹🇯", phoneCode: "+992" },
  { code: "TZ", name: "Tanzania", region: "Africa", flag: "🇹🇿", phoneCode: "+255" },
  { code: "TH", name: "Thailand", region: "Asia", flag: "🇹🇭", phoneCode: "+66" },
  { code: "TL", name: "Timor-Leste", region: "Asia", flag: "🇹🇱", phoneCode: "+670" },
  { code: "TG", name: "Togo", region: "Africa", flag: "🇹🇬", phoneCode: "+228" },
  { code: "TO", name: "Tonga", region: "Oceania", flag: "🇹🇴", phoneCode: "+676" },
  { code: "TT", name: "Trinidad and Tobago", region: "Americas", flag: "🇹🇹", phoneCode: "+1868" },
  { code: "TN", name: "Tunisia", region: "Africa", flag: "🇹🇳", phoneCode: "+216" },
  { code: "TR", name: "Turkey", region: "Europe", flag: "🇹🇷", phoneCode: "+90" },
  { code: "TM", name: "Turkmenistan", region: "Asia", flag: "🇹🇲", phoneCode: "+993" },
  { code: "TV", name: "Tuvalu", region: "Oceania", flag: "🇹🇻", phoneCode: "+688" },
  { code: "UG", name: "Uganda", region: "Africa", flag: "🇺🇬", phoneCode: "+256" },
  { code: "UA", name: "Ukraine", region: "Europe", flag: "🇺🇦", phoneCode: "+380" },
  { code: "AE", name: "United Arab Emirates", region: "Asia", flag: "🇦🇪", phoneCode: "+971" },
  { code: "GB", name: "United Kingdom", region: "Europe", flag: "🇬🇧", phoneCode: "+44" },
  { code: "US", name: "United States", region: "Americas", flag: "🇺🇸", phoneCode: "+1" },
  { code: "UY", name: "Uruguay", region: "Americas", flag: "🇺🇾", phoneCode: "+598" },
  { code: "UZ", name: "Uzbekistan", region: "Asia", flag: "🇺🇿", phoneCode: "+998" },
  { code: "VU", name: "Vanuatu", region: "Oceania", flag: "🇻🇺", phoneCode: "+678" },
  { code: "VA", name: "Vatican City", region: "Europe", flag: "🇻🇦", phoneCode: "+379" },
  { code: "VE", name: "Venezuela", region: "Americas", flag: "🇻🇪", phoneCode: "+58" },
  { code: "VN", name: "Vietnam", region: "Asia", flag: "🇻🇳", phoneCode: "+84" },
  { code: "YE", name: "Yemen", region: "Asia", flag: "🇾🇪", phoneCode: "+967" },
  { code: "ZM", name: "Zambia", region: "Africa", flag: "🇿🇲", phoneCode: "+260" },
  { code: "ZW", name: "Zimbabwe", region: "Africa", flag: "🇿🇼", phoneCode: "+263" },
] as const;

/**
 * Full phone country-code list derived from B2B_COUNTRIES.
 * Sorted A-Z by English country name. Used by B2BFormField phone select.
 * This is the single source of truth — B2B_PHONE_COUNTRY_CODES is kept only
 * for backward compatibility and should not be used in new code.
 */
export const B2B_PHONE_COUNTRY_CODES_FULL: readonly CountryCode[] =
  (B2B_COUNTRIES as readonly CountryData[])
    .filter((c): c is CountryData & { phoneCode: string } => Boolean(c.phoneCode))
    .map(c => ({ code: c.phoneCode as string, country: c.name, flag: c.flag }))
    .sort((a, b) => a.country.localeCompare(b.country, 'en', { sensitivity: 'base' }));

/**
 * Business-focused country list for participants and operations
 * Used in participants list and business operations
 */
export const B2B_BUSINESS_COUNTRIES: readonly string[] = [
  "United States",
  "United Kingdom", 
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Switzerland",
  "Canada",
  "Australia",
  "Japan",
  "Singapore",
  "United Arab Emirates",
  "Russia",
  "China",
  "India",
  "Brazil",
  "Mexico",
] as const;

/**
 * Utility functions for country data
 */
export const B2BCountryUtils = {
  /**
   * Get country by code
   */
  getCountryByCode: (code: string): CountryData | undefined => {
    return B2B_COUNTRIES.find(country => country.code === code);
  },

  /**
   * Get country by name
   */
  getCountryByName: (name: string): CountryData | undefined => {
    return B2B_COUNTRIES.find(country => country.name === name);
  },

  /**
   * Get phone country code by country code
   */
  getPhoneCodeByCountryCode: (countryCode: string): string | undefined => {
    const country = B2B_COUNTRIES.find(c => c.code === countryCode);
    return country?.phoneCode;
  },

  /**
   * Get countries by region
   */
  getCountriesByRegion: (region: string): readonly CountryData[] => {
    return B2B_COUNTRIES.filter(country => country.region === region);
  },

  /**
   * Get unique regions
   */
  getRegions: (): readonly string[] => {
    const regions = B2B_COUNTRIES.map(country => country.region);
    const uniqueRegions = Array.from(new Set(regions));
    return uniqueRegions;
  },

  /**
   * Search countries by name or code
   */
  searchCountries: (query: string): readonly CountryData[] => {
    const lowercaseQuery = query.toLowerCase();
    return B2B_COUNTRIES.filter(country => 
      country.name.toLowerCase().includes(lowercaseQuery) ||
      country.code.toLowerCase().includes(lowercaseQuery)
    );
  }
};
